import type { AwTableRow } from "@asap/design";
import type { PpeRequestSchema } from "@asap/shared/schemas/ppe-request";
import { usePpeRequestApi } from "~/components/ppe-request/composables/usePpeRequestApi";
import { usePpeRequestTable } from "~/components/ppe-request/composables/usePpeRequestTable";
import {
  getPpeRequestEmptyStateType,
  getPpeRequestRowActionKinds,
  type PpeRequestRowActionKind,
  type PpeRequestTabValue,
} from "~/components/ppe-request/ppe-request.model";
import type { TableRowAction } from "~/components/shared/table/TableRowActions.vue";
import { useManagees } from "~/components/team-member/composables/useManagees";
import { useUserMe } from "~/components/user/composables/useUser";
import { useToast } from "~/composables/useToast";
import type { TableEmptyStateType } from "~/utils/types";

export const usePpeRequestsPage = () => {
  const { listForTable, getById, update } = usePpeRequestApi();
  const table = usePpeRequestTable();
  const { ppeRequestTableState } = table;
  const { teamMember, isCSM } = useUserMe();
  const { getConsultantIdsFromSettingFilter } = useManagees();
  const { addToastError, addToastSuccess } = useToast();
  const { t } = useI18n();

  const ppeRequests = ref<PpeRequestSchema[]>([]);
  const rowSelection = ref<Record<string, boolean>>({});
  const tabValue = ref<PpeRequestTabValue>("all");
  const emptyStateType = ref<TableEmptyStateType>("no-data");
  const showCreateModal = ref(false);
  const openedPpeRequest = ref<PpeRequestSchema>();
  const ppeRequestToApprove = ref<PpeRequestSchema>();
  const isInitialized = ref(false);
  let latestFetchId = 0;
  const ppeRequestIdsBeingUpdated = new Set<string>();

  const fetchPpeRequests = async () => {
    const fetchId = ++latestFetchId;
    ppeRequestTableState.loading = true;
    try {
      const { items, totalItems } = await listForTable({
        ...ppeRequestTableState.selectedFilters,
        status: tabValue.value,
        sorting: ppeRequestTableState.sorting,
      });
      if (fetchId !== latestFetchId) return;
      ppeRequests.value = items;
      rowSelection.value = {};
      ppeRequestTableState.totalItems = totalItems;
      emptyStateType.value = getPpeRequestEmptyStateType(ppeRequestTableState.selectedFilters);
    } catch (error) {
      if (fetchId !== latestFetchId) return;
      emptyStateType.value = "error";
      addToastError({ title: t("ppeRequests.page.loadError") }, error);
    } finally {
      if (fetchId === latestFetchId) ppeRequestTableState.loading = false;
    }
  };

  const updatePpeRequestStatus = async (id: string, status: "pending" | "refused", successTitle: string) => {
    if (ppeRequestIdsBeingUpdated.has(id)) return;
    ppeRequestIdsBeingUpdated.add(id);
    try {
      await update(id, { status });
      const updatedPpeRequest = await getById(id);
      ppeRequests.value = updateCollection(ppeRequests.value, updatedPpeRequest);
      addToastSuccess({ title: successTitle });
    } catch (error) {
      addToastError({ title: t("ppeRequests.page.updateError") }, error);
    } finally {
      ppeRequestIdsBeingUpdated.delete(id);
    }
  };

  const rowActionsByKind: Record<PpeRequestRowActionKind, (ppeRequest: PpeRequestSchema) => TableRowAction> = {
    mark_as_pending: (ppeRequest) => ({
      label: t("ppeRequests.rowActions.markAsPending"),
      icon: "i-ph-arrows-counter-clockwise",
      onClick: () => updatePpeRequestStatus(ppeRequest.id, "pending", t("ppeRequests.rowActions.markedAsPending")),
    }),
    approve: (ppeRequest) => ({
      label: t("ppeRequests.rowActions.approve"),
      icon: "i-ph-check-circle",
      onClick: () => {
        ppeRequestToApprove.value = ppeRequest;
      },
    }),
    refuse: (ppeRequest) => ({
      label: t("ppeRequests.rowActions.refuse"),
      icon: "i-ph-x-circle",
      color: "critical",
      onClick: () => updatePpeRequestStatus(ppeRequest.id, "refused", t("ppeRequests.rowActions.refused")),
    }),
  };

  const getRowActions = (ppeRequest: PpeRequestSchema) =>
    getPpeRequestRowActionKinds(ppeRequest.status, isCSM.value).map((kind) => rowActionsByKind[kind](ppeRequest));

  const enableRowSelection = (row: AwTableRow<PpeRequestSchema>) => {
    const firstSelectedIndex = Object.keys(rowSelection.value).at(0);
    if (firstSelectedIndex === undefined) return true;
    return row.original.status === ppeRequests.value.at(Number(firstSelectedIndex))?.status;
  };

  const closeApproveDialog = () => {
    ppeRequestToApprove.value = undefined;
  };

  const handlePpeRequestApproved = () => {
    closeApproveDialog();
    fetchPpeRequests();
  };

  const handleSheetChange = (changes: Partial<PpeRequestSchema>) => {
    if (!openedPpeRequest.value) return;
    openedPpeRequest.value = { ...openedPpeRequest.value, ...changes };
    ppeRequests.value = updateCollection(ppeRequests.value, openedPpeRequest.value);
  };

  onMounted(async () => {
    ppeRequestTableState.loading = true;
    // Typed filters survive navigation; pagination and settings-driven filters start over.
    ppeRequestTableState.selectedFilters.pageIndex = 0;
    ppeRequestTableState.selectedFilters.businessUnitIds = useLocalStorage<string[]>(
      "settings_business_unit_ids",
      teamMember.value?.business_unit_id ? [teamMember.value.business_unit_id] : []
    ).value;
    try {
      ppeRequestTableState.selectedFilters.consultantTeamMemberIds = await getConsultantIdsFromSettingFilter();
    } catch (error) {
      addToastError({ title: t("ppeRequests.page.consultantsLoadError") }, error);
    }
    await fetchPpeRequests();
    isInitialized.value = true;
  });

  watch(
    () => ppeRequestTableState.selectedFilters.pageIndex,
    () => {
      if (!isInitialized.value) return;
      fetchPpeRequests();
    }
  );

  watch(
    () => [
      ppeRequestTableState.sorting,
      ppeRequestTableState.selectedFilters.search,
      ppeRequestTableState.selectedFilters.selectedDate,
      ppeRequestTableState.selectedFilters.businessUnitIds,
      ppeRequestTableState.selectedFilters.wsmTeamMemberIds,
      ppeRequestTableState.selectedFilters.consultantTeamMemberIds,
      ppeRequestTableState.selectedFilters.pageSize,
      tabValue.value,
    ],
    () => {
      if (!isInitialized.value) return;
      // The pageIndex watcher fetches: resetting it here and fetching too would fetch twice.
      if (ppeRequestTableState.selectedFilters.pageIndex !== 0) {
        ppeRequestTableState.selectedFilters.pageIndex = 0;
        return;
      }
      fetchPpeRequests();
    }
  );

  return {
    ...table,
    ppeRequests,
    rowSelection,
    tabValue,
    emptyStateType,
    showCreateModal,
    openedPpeRequest,
    ppeRequestToApprove,
    fetchPpeRequests,
    getRowActions,
    enableRowSelection,
    closeApproveDialog,
    handlePpeRequestApproved,
    handleSheetChange,
  };
};
